/*
 * � 2025, NTT Data Group. All rights reserved. See LICENSE.txt file in the project root for third party licenses info.
 */

import { Component, OnDestroy, OnInit } from "@angular/core";
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { ActivatedRoute, Router, RouterOutlet } from "@angular/router";
import { Subject, takeUntil, tap } from "rxjs";
import { ButtonComponent } from "../../shared/components/button/button.component";
import { DropdownComponent } from "../../shared/components/dropdown/dropdown.component";
import { SelectOption } from "../../shared/models/ui/select-option.model";
import { PATHS, RESOURCE_TYPES } from "../../shared/utils/constants";

@Component({
	selector: "globalictcc-whatif",
	standalone: true,
	imports: [ButtonComponent, ReactiveFormsModule, DropdownComponent, RouterOutlet],
	templateUrl: "./whatif.component.html",
	styleUrl: "./whatif.component.scss"
})
export class WhatifComponent implements OnInit, OnDestroy {
	public resourceForm = new FormGroup({
		resourceType: new FormControl("", [Validators.required])
	});

	public resourceTypes: SelectOption[] = RESOURCE_TYPES;

	private _destroy$ = new Subject<void>();

	constructor(
		private _router: Router,
		private _route: ActivatedRoute
	) {}

	ngOnInit() {
		this._onQueryParamsChange();
	}

	ngOnDestroy() {
		this._destroy$.next();
		this._destroy$.complete();
	}

	/**
	 * Subscribes to changes in the route's query parameters and updates the `resourceType`
	 * control in the `resourceForm` accordingly. If the `resourceType` is present in the
	 * query parameters, it sets the form control to that value. Otherwise, it attempts to
	 * extract the `resourceType` from the current URL path and sets the form control if found.
	 * The subscription is automatically unsubscribed when the component is destroyed.
	 *
	 * @private
	 */
	private _onQueryParamsChange() {
		this._route.queryParams
			.pipe(
				tap({
					next: queryParams => {
						const { resourceType } = queryParams;
						if (resourceType) {
							this.resourceForm.controls.resourceType.setValue(resourceType);
						} else {
							const resourceType = this._router.url.split("/").pop();
							if (resourceType)
								this.resourceForm.controls.resourceType.setValue(resourceType);
						}
					}
				}),
				takeUntil(this._destroy$)
			)
			.subscribe();
	}

	/**
	 * Navigates back to the previous page using the environment service.
	 * This method is typically called when the user wants to go back to the previous view.
	 */
	public onGoBack() {
		this._router.navigate([PATHS.TOOLBOX]);
	}

	/**
	 * Handles the application of a selected resource type from the resource form.
	 *
	 * Retrieves the selected resource type from the form controls. If a resource type is selected,
	 * navigates to the corresponding route using the Angular Router, appending the resource type
	 * to the WHAT_IF path. If no resource type is selected, the method returns without performing any action.
	 */
	public onResourceApply() {
		const resourceType = this.resourceForm.controls.resourceType.value;

		if (!resourceType) return;

		this._router.navigate([`${PATHS.WHAT_IF}/${resourceType}`]);
	}
}
